/* eslint-disable camelcase */
import { EspialEventTypes } from '../../../../types/Espial/EspialEventTypes';
import { EspialPurchaseItem } from '../../../../types/Espial/EspialPurchaseItem';
import { VehicleObject } from '../../../../types/myVehiclesType';
import { isEspialDisabledPath } from '../../shared/utils/espial.utils';

const useEspial = () => {
  const isEspialDisabled = () => {
    if (typeof window === 'undefined') {
      return false;
    }

    return isEspialDisabledPath(window.location.pathname);
  };

  const createEventPayload = (params?: Record<string, any>[] | null) => ({
    props: Object.fromEntries(
      (Array.isArray(params) ? params : [])
        .filter((obj) => obj && typeof obj === 'object')
        .flatMap((obj) => Object.entries(obj)),
    ),
  });

  const createPurchaseEventPayload = (
    items: {
      number: number;
      productName: string;
      productNumber: string;
      productPrice: number;
    }[],
  ) => {
    const getSku = (productNumber: string) => {
      if (productNumber.includes('1111')) {
        return '1111';
      }

      if (productNumber.includes('1121')) {
        return '1121';
      }

      return productNumber;
    };

    const payload = {
      revenue: items.map<EspialPurchaseItem>((item) => ({
        amount: item.productPrice,
        id: item.productName,
        quantity: item.number,
        sku: getSku(item.productNumber),
      })),
    };

    return Object.fromEntries(Object.entries(payload));
  };

  const trackEvent = (eventType: EspialEventTypes, params?: Record<string, any>[]) => {
    if (typeof window === 'undefined' || isEspialDisabled()) {
      return;
    }

    window?.espial?.trigger(eventType, createEventPayload(params));
  };

  const trackPurchase = (
    items: {
      number: number;
      productName: string;
      productNumber: string;
      productPrice: number;
    }[],
  ) => {
    if (typeof window === 'undefined' || isEspialDisabled()) {
      return;
    }

    const payload = createPurchaseEventPayload(items);

    window?.espial?.trigger(EspialEventTypes.PURCHASE, payload);
  };

  const trackVehicleAdded = (vehicle: VehicleObject) => {
    trackEvent(EspialEventTypes.VEHICLE_ADDED, [
      { vehicle_type: vehicle.vehicleType },
      { vehicle_brand: vehicle.manufactureName },
      { vehicle_model: vehicle.modelName },
      { vehicle_year: vehicle.year },
      { vehicle_is_electric: vehicle.isEv },
    ]);
  };

  const trackVehicleRemoved = (vehicle: VehicleObject) => {
    trackEvent(EspialEventTypes.VEHICLE_REMOVED, [
      { vehicle_type: vehicle.vehicleType },
      { vehicle_brand: vehicle.manufactureName },
      { vehicle_model: vehicle.modelName },
      { vehicle_year: vehicle.year },
      { vehicle_is_electric: vehicle.isEv },
    ]);
  };

  const trackNafCenterReportOpened = () => {
    trackEvent(EspialEventTypes.NAF_CENTER_REPORT_OPENED);
  };

  const trackVehicleContractOpened = () => {
    trackEvent(EspialEventTypes.VEHICLE_CONTRACT_OPENED);
  };

  const trackVehicleAccidentStatementOpened = () => {
    trackEvent(EspialEventTypes.VEHICLE_ACCIDENT_STATEMENT_OPENED);
  };

  const trackLogin = () => {
    trackEvent(EspialEventTypes.LOGIN);
  };

  const trackLogout = () => {
    trackEvent(EspialEventTypes.LOGOUT);
  };

  const trackMenuClick = (params: Record<string, any>[]) => {
    trackEvent(EspialEventTypes.MENU_CLICK, params);
  };

  const trackMembershipUpgraded = () => {
    trackEvent(EspialEventTypes.MEMBERSHIP_UPGRADED);
  };

  const trackHouseholdMemberAdded = () => {
    trackEvent(EspialEventTypes.HOUSEHOLD_MEMBER_ADDED);
  };

  return {
    createEventPayload,
    createPurchaseEventPayload,
    trackEvent,
    trackPurchase,
    trackVehicleAdded,
    trackVehicleRemoved,
    trackNafCenterReportOpened,
    trackVehicleContractOpened,
    trackVehicleAccidentStatementOpened,
    trackLogin,
    trackLogout,
    trackMenuClick,
    trackMembershipUpgraded,
    trackHouseholdMemberAdded,
  };
};

export default useEspial;
